import type { FormEventHandler } from 'react';
import { CalculatorActions } from 'components/Widget/shared/CalculatorActions';
import { CALCULATOR_FORM_CARD } from 'components/Widget/shared/cardStyles';
import { CalculatorInput } from 'components/Widget/CalculatorInput/CalculatorInput';
import { SelectField } from 'components/Widget/shared/SelectField';
import type { CurrencyFieldProps } from 'components/Widget/shared/useCurrencyFields';
import type { PercentFieldProps } from 'components/Widget/shared/usePercentFields';
import type { CalculatorFieldErrors } from 'components/Widget/shared/types';
import {
  APY_FIELD,
  CONTRIBUTION_FREQUENCY_FIELD,
  CONTRIBUTION_FREQUENCY_OPTIONS,
  RECURRING_CONTRIBUTION_FIELD,
  SAVINGS_PERIOD_FIELD,
  SAVINGS_PERIOD_OPTIONS,
  STARTING_BALANCE_FIELD,
  TITLE,
} from './constants';
import type { ContributionFrequencyKey, SavingsPeriodKey } from './types';

interface ApySavingsCalculatorFormProps {
  errors: CalculatorFieldErrors;
  currencyFieldProps: CurrencyFieldProps;
  apyFieldProps: PercentFieldProps;
  contributionFrequency: ContributionFrequencyKey;
  savingsPeriod: SavingsPeriodKey;
  onContributionFrequencyChange: (key: ContributionFrequencyKey) => void;
  onSavingsPeriodChange: (key: SavingsPeriodKey) => void;
  onCalculate: () => void;
  onReset: () => void;
}

export const ApySavingsCalculatorForm = ({
  errors,
  currencyFieldProps,
  apyFieldProps,
  contributionFrequency,
  savingsPeriod,
  onContributionFrequencyChange,
  onSavingsPeriodChange,
  onCalculate,
  onReset,
}: ApySavingsCalculatorFormProps) => {
  const handleSubmit: FormEventHandler<HTMLFormElement> = event => {
    event.preventDefault();
    onCalculate();
  };

  return (
    <form aria-label={TITLE} className={CALCULATOR_FORM_CARD} onSubmit={handleSubmit}>
      <div className="grid gap-6 sm:grid-cols-2">
        {/* Native validation would pre-empt the calculator's custom range rules. */}
        <CalculatorInput
          {...currencyFieldProps(STARTING_BALANCE_FIELD, errors[STARTING_BALANCE_FIELD.label])}
          collapsibleHint
        />
        <CalculatorInput
          {...currencyFieldProps(RECURRING_CONTRIBUTION_FIELD, errors[RECURRING_CONTRIBUTION_FIELD.label])}
          collapsibleHint
        />
      </div>

      <SelectField
        field={CONTRIBUTION_FREQUENCY_FIELD}
        options={CONTRIBUTION_FREQUENCY_OPTIONS}
        value={contributionFrequency}
        onChange={onContributionFrequencyChange}
      />

      <SelectField
        field={SAVINGS_PERIOD_FIELD}
        options={SAVINGS_PERIOD_OPTIONS}
        value={savingsPeriod}
        onChange={onSavingsPeriodChange}
      />

      <CalculatorInput {...apyFieldProps(APY_FIELD, errors[APY_FIELD.label])} collapsibleHint />

      <CalculatorActions onReset={onReset} />
    </form>
  );
};
